import React from 'react';
import { Provider } from 'react-redux';
import ReactDOM from 'react-dom/client';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider } from './context/ThemeContext';
import { WhiteLabelProvider } from './context/WhiteLabelContext';

import './index.scss';
import App from './App';
import reportWebVitals from './reportWebVitals';
import store from './redux/store';
import { config } from './config';
import { checkDomainAccess, createRequestHeaders } from './utils/domainUtils';

// Domain restriction check is now handled by centralized utility

// Fetch white label configuration to get the correct Google client ID
const getWhiteLabelConfig = async () => {
  try {
    const hostname = window.location.hostname;

    // Skip API call for localhost/development domains
    if (hostname === 'localhost' || hostname.startsWith('localhost:') ||
        hostname === '127.0.0.1' || hostname.startsWith('127.0.0.1:')) {
      return null;
    }

    const headers = createRequestHeaders();

    const response = await fetch(`${config.API_URI}/v1/white-label/branding`, {
      method: 'GET',
      credentials: 'include',
      headers: headers,
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.error('Error fetching white label config:', error);
  }

  return null;
};

const initializeApp = async () => {

  const hasAccess = await checkDomainAccess();

  if (!hasAccess) {
    window.location.href = config.DOMAIN;
    return;
  }

  // Fetch white label configuration to determine the correct Google client ID
  const whiteLabelConfig = await getWhiteLabelConfig();

  // Determine Google OAuth configuration
  let googleClientId = config.GOOGLE_AUTH_CLIENT_ID; // Default Zoobbe client ID

  if (whiteLabelConfig && whiteLabelConfig.isWhiteLabel) {
    // For white label domains, check if Google OAuth is enabled and has custom client ID
    if (whiteLabelConfig.oauth?.enableGoogleAuth && whiteLabelConfig.oauth?.googleClientId) {
      googleClientId = whiteLabelConfig.oauth.googleClientId;
    } 
  } 

  const root = ReactDOM.createRoot(document.getElementById('root'));

  // Always wrap with GoogleOAuthProvider to prevent hook errors
  // The actual OAuth functionality is controlled by the WhiteLabelContext
  const AppWithProviders = () => (
    <Provider store={store}>
      <GoogleOAuthProvider clientId={googleClientId}>
        <WhiteLabelProvider>
          <ThemeProvider>
            <App />
          </ThemeProvider>
        </WhiteLabelProvider>
      </GoogleOAuthProvider>
    </Provider>
  );

  root.render(<AppWithProviders />);
};

// Suppress ResizeObserver loop errors that occur during rapid DOM updates
// This is a known issue with ResizeObserver when DOM changes happen faster than notifications can be delivered
const originalConsoleError = console.error;
console.error = (...args) => {
  // Suppress specific ResizeObserver errors that are harmless but noisy
  if (
    args.length > 0 &&
    typeof args[0] === 'string' &&
    args[0].includes('ResizeObserver loop completed with undelivered notifications')
  ) {
    return; // Suppress this specific error
  }
  // Allow all other errors to be logged normally
  // originalConsoleError.apply(console, args);
};

// Also handle the error event for ResizeObserver
window.addEventListener('error', (event) => {
  if (event.message && event.message.includes('ResizeObserver loop completed with undelivered notifications')) {
    event.preventDefault(); // Prevent the error from being logged
    return false;
  }
});

initializeApp();



// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
